
import {
  fixStudentDuplications as fixStudentDuplicationsApi,
  fixStudentSubCollectionTransfer as fixStudentSubCollectionTransferApi,
  fixStudentClassroomIdInConversations as fixStudentClassroomIdInConversationsApi,
  fixStudentJournalReflections as fixStudentJournalReflectionsApi,
  FixSubCollectionTransferResponseItem,
  FixStudentDuplicationsResponseItem,
  FixClassroomIdResponseItem,
  FixJournalReflectionResponseItem
} from "@/api/classroomTransfer";
import { findSchoolByNameOrDocId } from "@/api/school";
import SubCollectionResultModal from "@/components/classroom_transfer_fix/SubCollectionResultModal.vue";
import DuplicationResultModal from "@/components/classroom_transfer_fix/DuplicationResultModal.vue";
import ConversationResultModal from "@/components/classroom_transfer_fix/ConversationResultModal.vue";
import JournalResultModal from "@/components/classroom_transfer_fix/JournalResultModal.vue";
import MTextField from "@/components/form/MTextField.vue";
import MButton from "@/components/MButton.vue";
import { School } from "@/entities/school";
import store from "@/store";
import { Options, Vue } from "vue-class-component";

@Options({
  components: {
    SubCollectionResultModal,
    DuplicationResultModal,
    ConversationResultModal,
    JournalResultModal,
    MButton,
    MTextField
  }
})
export default class HandleClassroomTransferError extends Vue {
  name = "";
  schoolDocId = "";
  foundSchools: (School & { ownerName: string })[] = [];
  hasSearched = false;
  selectedSchoolDocIds: string[] = [];
  // 選択済みスクールの名前をキャッシュ（検索結果が変わっても名前を表示できるように）
  schoolNameCache: Map<string, string> = new Map();
  authToken = "";

  // サブコレクション移動修正結果モーダル用
  showSubCollectionResultModal = false;
  subCollectionResults: FixSubCollectionTransferResponseItem[] = [];

  // 重複修正結果モーダル用
  showDuplicationResultModal = false;
  duplicationResults: FixStudentDuplicationsResponseItem[] = [];

  // 会話の教室ID修正結果モーダル用
  showConversationResultModal = false;
  conversationResults: FixClassroomIdResponseItem[] = [];

  // ジャーナルの教室ID修正結果モーダル用
  showJournalResultModal = false;
  journalResults: FixJournalReflectionResponseItem[] = [];

  get showNoResultsMessage() {
    return this.hasSearched && this.foundSchools.length === 0;
  }

  get schoolNameMap(): Record<string, string> {
    const map: Record<string, string> = {};
    // 検索結果から
    for (const school of this.foundSchools) {
      map[school.ref.id] = school.data.name;
    }
    // キャッシュから
    this.schoolNameCache.forEach((name, docId) => {
      if (!map[docId]) {
        map[docId] = name;
      }
    });
    return map;
  }

  isSelected(school: School & { ownerName: string }): boolean {
    return this.selectedSchoolDocIds.includes(school.ref.id);
  }

  toggleSchoolSelection(school: School & { ownerName: string }) {
    const docId = school.ref.id;
    const index = this.selectedSchoolDocIds.indexOf(docId);

    if (index === -1) {
      this.selectedSchoolDocIds.push(docId);
      // 名前をキャッシュに保存
      this.schoolNameCache.set(docId, school.data.name);
    } else {
      this.selectedSchoolDocIds.splice(index, 1);
    }
  }

  removeSelectedSchool(docId: string) {
    const index = this.selectedSchoolDocIds.indexOf(docId);
    if (index !== -1) {
      this.selectedSchoolDocIds.splice(index, 1);
    }
  }

  clearAllSelected() {
    this.selectedSchoolDocIds = [];
  }

  selectAllSearchResults() {
    for (const school of this.foundSchools) {
      const docId = school.ref.id;
      if (!this.selectedSchoolDocIds.includes(docId)) {
        this.selectedSchoolDocIds.push(docId);
        this.schoolNameCache.set(docId, school.data.name);
      }
    }
  }

  moveToHome() {
    this.$router.push({ name: "home" });
  }

  async searchByName() {
    if (!this.name) return;

    try {
      store.commit("START_LOADING", "検索中...");
      this.foundSchools = await findSchoolByNameOrDocId({ name: this.name });
      this.hasSearched = true;
      // 検索結果のスクール名をキャッシュに追加
      for (const school of this.foundSchools) {
        this.schoolNameCache.set(school.ref.id, school.data.name);
      }
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
    } finally {
      store.commit("END_LOADING");
    }
  }

  async searchByDocId() {
    if (!this.schoolDocId) return;

    try {
      store.commit("START_LOADING", "検索中...");
      this.foundSchools = await findSchoolByNameOrDocId({
        docId: this.schoolDocId
      });
      this.hasSearched = true;
      // 検索結果のスクール名をキャッシュに追加
      for (const school of this.foundSchools) {
        this.schoolNameCache.set(school.ref.id, school.data.name);
      }
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
    } finally {
      store.commit("END_LOADING");
    }
  }

  async fixStudentDuplications() {
    if (this.selectedSchoolDocIds.length === 0) {
      return;
    }

    if (this.selectedSchoolDocIds.length > 1) {
      alert("いったん複数選択での実行はできないようにしています。");
      return;
    }

    if (!this.authToken) {
      return;
    }

    try {
      store.commit("START_LOADING", "処理中...");
      const results = await fixStudentDuplicationsApi(
        { schoolDocId: this.selectedSchoolDocIds[0] },
        this.authToken
      );

      // 結果をモーダルで表示
      this.duplicationResults = results;
      this.showDuplicationResultModal = true;
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "unknown error";
      alert(`エラーが発生しました。\n${message}`);
    } finally {
      store.commit("END_LOADING");
    }
  }

  async fixStudentSubCollectionTransfer() {
    if (this.selectedSchoolDocIds.length === 0) {
      return;
    }

    if (this.selectedSchoolDocIds.length > 1) {
      alert("いったん複数選択での実行はできないようにしています。");
      return;
    }

    if (!this.authToken) {
      return;
    }

    try {
      store.commit("START_LOADING", "処理中...");
      const results = await fixStudentSubCollectionTransferApi(
        { schoolDocId: this.selectedSchoolDocIds[0] },
        this.authToken
      );

      // 結果をモーダルで表示
      this.subCollectionResults = results;
      this.showSubCollectionResultModal = true;
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "unknown error";
      alert(`エラーが発生しました。\n${message}`);
    } finally {
      store.commit("END_LOADING");
    }
  }

  closeSubCollectionResultModal() {
    this.showSubCollectionResultModal = false;
    this.subCollectionResults = [];
  }

  closeDuplicationResultModal() {
    this.showDuplicationResultModal = false;
    this.duplicationResults = [];
  }

  closeConversationResultModal() {
    this.showConversationResultModal = false;
    this.conversationResults = [];
  }

  closeJournalResultModal() {
    this.showJournalResultModal = false;
    this.journalResults = [];
  }

  async fixStudentClassroomIdInConversations() {
    if (this.selectedSchoolDocIds.length === 0) {
      return;
    }

    if (this.selectedSchoolDocIds.length > 1) {
      alert("いったん複数選択での実行はできないようにしています。");
      return;
    }

    if (!this.authToken) {
      return;
    }

    try {
      store.commit("START_LOADING", "処理中...");
      const results = await fixStudentClassroomIdInConversationsApi(
        { schoolDocId: this.selectedSchoolDocIds[0] },
        this.authToken
      );

      // 結果をモーダルで表示
      this.conversationResults = results;
      this.showConversationResultModal = true;
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "unknown error";
      alert(`エラーが発生しました。\n${message}`);
    } finally {
      store.commit("END_LOADING");
    }
  }

  async fixStudentJournalReflections() {
    if (this.selectedSchoolDocIds.length === 0) {
      return;
    }

    if (this.selectedSchoolDocIds.length > 1) {
      alert("いったん複数選択での実行はできないようにしています。");
      return;
    }

    if (!this.authToken) {
      return;
    }

    try {
      store.commit("START_LOADING", "処理中...");
      const results = await fixStudentJournalReflectionsApi(
        { schoolDocId: this.selectedSchoolDocIds[0] },
        this.authToken
      );

      // 結果をモーダルで表示
      this.journalResults = results;
      this.showJournalResultModal = true;
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "unknown error";
      alert(`エラーが発生しました。\n${message}`);
    } finally {
      store.commit("END_LOADING");
    }
  }
}
